// ============================================================================
// ActiveSessionsDock - persistent "your tables" dock for multi-table UX
// ============================================================================
// Bottom-left fixed dock. One card per table you're seated at. Cards visually
// escalate:
//   • Waiting        - muted slate card, no border
//   • Hand running   - subtle cyan border, opponent name shown
//   • YOUR TURN      - bright cyan pulsing border, "ACT NOW" tag, audio ping
//                      on first transition into this state
//
// Click any card to navigate to that table. If you're currently viewing one
// of these tables it's highlighted as "current". Disappears entirely when
// you have no seated tables, so it doesn't clutter the lobby for new users.
//
// Designed to coexist with AdminTableControls (bottom-right) without overlap.
// Hidden on /game/* pages since the table view itself is the UI focus.
// ============================================================================
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, HStack, VStack, Text, Badge, IconButton } from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { Trophy, Coins, ChevronUp, ChevronDown, ArrowRight } from 'lucide-react';
import { useMySeatedTables, MySeatedTable } from '../../hooks/useMySeatedTables';
import { POKER_COLORS } from '../../theme/designSystem';

interface ActiveSessionsDockProps {
  currentUserId: string | null;
}

const pulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 ${POKER_COLORS.cyan}66, 0 8px 24px rgba(0,0,0,0.5); }
  50%      { box-shadow: 0 0 0 6px ${POKER_COLORS.cyan}00, 0 8px 24px rgba(0,0,0,0.5); }
`;

const ActiveSessionsDock: React.FC<ActiveSessionsDockProps> = ({ currentUserId }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const tables = useMySeatedTables(currentUserId);
  const [collapsed, setCollapsed] = useState(true);
  // Phones: fully minimize the dock to a small tab. It sits over the felt,
  // and on a 6-inch screen 'collapsed' still ate real estate.
  const [minimized, setMinimized] = useState<boolean>(() => {
    try { return localStorage.getItem('pss:myTablesMinimized') === '1'; } catch { return false; }
  });
  const setMin = (v: boolean) => {
    setMinimized(v);
    try { localStorage.setItem('pss:myTablesMinimized', v ? '1' : '0'); } catch { /* noop */ }
  };
  // Track which tables we've already pinged to avoid retriggering audio on
  // every snapshot update while it's still our turn at the same table.
  const lastPingedRef = useRef<Map<string, boolean>>(new Map());

  const turnCount = tables.filter(t => t.myTurn).length;
  const currentViewingTableId = location.pathname.startsWith('/game/')
    ? location.pathname.split('/game/')[1]?.split('?')[0]
    : null;

  // Audio ping on the rising edge of YOUR TURN. Quiet click - not obnoxious.
  useEffect(() => {
    if (tables.length === 0) return;
    tables.forEach(t => {
      const was = lastPingedRef.current.get(t.tableId) || false;
      if (t.myTurn && !was) {
        try {
          const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = 'sine';
          osc.frequency.value = 880;
          gain.gain.setValueAtTime(0.001, ctx.currentTime);
          gain.gain.exponentialRampToValueAtTime(0.08, ctx.currentTime + 0.02);
          gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.25);
          osc.connect(gain).connect(ctx.destination);
          osc.start();
          osc.stop(ctx.currentTime + 0.26);
        } catch { /* audio not available */ }
      }
      lastPingedRef.current.set(t.tableId, t.myTurn);
    });
  }, [tables]);

  // 🔔 Tab-title alert: when it's your turn at ANY table and this window
  // is backgrounded (or you're looking at a different table), the browser
  // tab itself says so - multitablers see it across every window.
  const origTitleRef = useRef<string | null>(null);
  useEffect(() => {
    const turnElsewhere = tables.some(t => t.myTurn && t.tableId !== currentViewingTableId);
    const anyTurn = tables.some(t => t.myTurn);
    if ((anyTurn && document.hidden) || turnElsewhere) {
      if (origTitleRef.current === null) origTitleRef.current = document.title;
      document.title = 'YOUR TURN - PokerSweepStars';
    } else if (origTitleRef.current !== null) {
      document.title = origTitleRef.current;
      origTitleRef.current = null;
    }
    return () => {
      if (origTitleRef.current !== null) {
        document.title = origTitleRef.current;
        origTitleRef.current = null;
      }
    };
  }, [tables, currentViewingTableId]);

  // 📌 Draggable: grab the header and put the dock wherever you like.
  // Position persists per device.
  const [dockPos, setDockPos] = useState<{ x: number; y: number }>(() => {
    try {
      const saved = localStorage.getItem('pss:myTablesDockPos');
      if (saved) {
        const p = JSON.parse(saved);
        if (typeof p?.x === 'number' && typeof p?.y === 'number') return p;
      }
    } catch { /* default below */ }
    // Phones: start below the mobile chrome bar so the pill never covers
    // the table name. Still draggable; a dragged position persists.
    if (typeof window !== 'undefined' && window.matchMedia('(max-width: 600px)').matches) {
      return { x: 8, y: 104 };
    }
    return { x: 20, y: 80 };
  });
  const dragRef = useRef<{ startX: number; startY: number; baseX: number; baseY: number; moved: boolean } | null>(null);
  const suppressClickRef = useRef(false);

  const onDragStart = (e: React.PointerEvent) => {
    dragRef.current = { startX: e.clientX, startY: e.clientY, baseX: dockPos.x, baseY: dockPos.y, moved: false };
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
  };
  const onDragMove = (e: React.PointerEvent) => {
    const d = dragRef.current;
    if (!d) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    if (Math.abs(dx) + Math.abs(dy) > 4) d.moved = true;
    setDockPos({
      x: Math.min(Math.max(4, d.baseX + dx), window.innerWidth - 130),
      y: Math.min(Math.max(4, d.baseY + dy), window.innerHeight - 60),
    });
  };
  const onDragEnd = () => {
    const d = dragRef.current;
    dragRef.current = null;
    if (d?.moved) {
      suppressClickRef.current = true;
      try { localStorage.setItem('pss:myTablesDockPos', JSON.stringify(dockPos)); } catch { /* noop */ }
    }
  };

  // Hide entirely when there are no seated tables - no chrome for new users.
  if (tables.length === 0) return null;

  // Visible on every page (including /game/*) so multi-tablers can jump
  // between their tables and spectators can hop back to their seat. The
  // "CURRENTLY VIEWING" tag on the row prevents confusion about which
  // table the page is showing.

  if (minimized) {
    return (
      <Box
        position="fixed"
        top={`${dockPos.y}px`}
        left={`${dockPos.x}px`}
        zIndex={9050}
        as="button"
        onClick={() => setMin(false)}
        bg="rgba(13, 31, 46, 0.95)"
        border="1px solid"
        borderColor={turnCount > 0 ? POKER_COLORS.cyan : `${POKER_COLORS.cyan}33`}
        borderRadius="full"
        px={2.5}
        py={1}
        boxShadow="0 4px 16px rgba(0,0,0,0.45)"
        aria-label="Show my tables"
      >
        <HStack spacing={1.5}>
          <Text fontSize="11px" fontWeight="bold" color={POKER_COLORS.cyan}>
            {tables.length}
          </Text>
          {turnCount > 0 && (
            <Box w="7px" h="7px" borderRadius="full" bg={POKER_COLORS.cyan}
              animation={`${pulse} 1.4s ease-in-out infinite`} />
          )}
          <ChevronDown size={12} color={POKER_COLORS.cyan} style={{ transform: 'rotate(-90deg)' }} />
        </HStack>
      </Box>
    );
  }

  return (
    <Box
      position="fixed"
      top={`${dockPos.y}px`}
      left={`${dockPos.x}px`}
      zIndex={9050}
      maxW="320px"
    >
      {/* Header / collapse toggle */}
      <Box
        bg="rgba(13, 31, 46, 0.95)"
        backdropFilter="blur(10px)"
        border="1px solid"
        borderColor={turnCount > 0 ? POKER_COLORS.cyan : `${POKER_COLORS.cyan}33`}
        borderRadius="lg"
        overflow="hidden"
        boxShadow={turnCount > 0
          ? `0 0 0 1px ${POKER_COLORS.cyan}66, 0 8px 32px rgba(0,0,0,0.5)`
          : '0 8px 32px rgba(0,0,0,0.4)'}
      >
        <HStack
          px={3} py={2}
          spacing={2}
          cursor="grab"
          style={{ touchAction: 'none' }}
          onPointerDown={onDragStart}
          onPointerMove={onDragMove}
          onPointerUp={onDragEnd}
          onPointerCancel={onDragEnd}
          onClick={() => {
            // A drag must not toggle the collapse.
            if (suppressClickRef.current) { suppressClickRef.current = false; return; }
            setCollapsed(c => !c);
          }}
          borderBottom={collapsed ? 'none' : '1px solid'}
          borderColor={`${POKER_COLORS.cyan}22`}
        >
          <Text fontSize="xs" color={POKER_COLORS.cyan} fontWeight="bold" letterSpacing="wider">
            MY TABLES
          </Text>
          <Badge
            bg={turnCount > 0 ? POKER_COLORS.cyan : `${POKER_COLORS.cyan}22`}
            color={turnCount > 0 ? POKER_COLORS.darkTeal : POKER_COLORS.cyan}
            fontSize="9px"
            fontWeight="bold"
            px={1.5}
          >
            {tables.length}
          </Badge>
          {turnCount > 0 && (
            <Badge bg="orange.500" color="white" fontSize="9px" fontWeight="bold" px={1.5}>
              {turnCount} TURN{turnCount > 1 ? 'S' : ''}
            </Badge>
          )}
          <Box flex={1} />
          <IconButton
            aria-label="Minimize my tables"
            size="xs"
            variant="ghost"
            color={POKER_COLORS.cyan}
            _hover={{ bg: `${POKER_COLORS.cyan}22` }}
            icon={<Text fontSize="14px" lineHeight="1" fontWeight="bold">&ndash;</Text>}
            onClick={(e) => { e.stopPropagation(); setMin(true); }}
          />
          <IconButton
            size="xs"
            variant="ghost"
            aria-label="Toggle"
            color="gray.400"
            icon={collapsed ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          />
        </HStack>

        {!collapsed && (
          <VStack spacing={0} align="stretch" maxH="60vh" overflowY="auto">
            {tables.map(t => (
              <SessionRow
                key={t.tableId}
                table={t}
                isCurrent={currentViewingTableId === t.tableId}
                onClick={() => navigate(`/game/${t.tableId}`)}
              />
            ))}
          </VStack>
        )}
      </Box>
    </Box>
  );
};

interface SessionRowProps {
  table: MySeatedTable;
  isCurrent: boolean;
  onClick: () => void;
}

const SessionRow: React.FC<SessionRowProps> = ({ table, isCurrent, onClick }) => {
  const turnState = table.myTurn ? 'turn' : table.handInProgress ? 'playing' : 'waiting';

  const cardBg = isCurrent
    ? `${POKER_COLORS.cyan}11`
    : turnState === 'turn'
    ? `${POKER_COLORS.cyan}1a`
    : 'transparent';
  const borderTopColor = isCurrent
    ? POKER_COLORS.cyan
    : `${POKER_COLORS.cyan}11`;

  return (
    <Box
      px={3} py={2.5}
      cursor="pointer"
      onClick={onClick}
      bg={cardBg}
      borderTop="1px solid"
      borderTopColor={borderTopColor}
      transition="all 0.15s"
      _hover={{ bg: `${POKER_COLORS.cyan}22` }}
      role="group"
      animation={turnState === 'turn' && !isCurrent ? `${pulse} 1.6s ease-in-out infinite` : undefined}
    >
      <HStack spacing={2} align="start">
        <Box pt="2px">
          {table.isTournament
            ? <Trophy size={14} color={POKER_COLORS.gold} />
            : <Coins size={14} color={POKER_COLORS.cyan} />}
        </Box>

        <VStack spacing={0} align="start" flex={1} minW={0}>
          <Text
            fontSize="sm"
            fontWeight={turnState === 'turn' ? 'bold' : 'semibold'}
            color="white"
            isTruncated
            w="100%"
          >
            {table.name}
          </Text>

          {turnState === 'turn' ? (
            <Text fontSize="xs" color={POKER_COLORS.cyan} fontWeight="bold" letterSpacing="wide">
              YOUR TURN · ACT NOW
            </Text>
          ) : turnState === 'playing' ? (
            <Text fontSize="xs" color="gray.500" isTruncated w="100%">
              Hand in play{table.currentPlayerName ? ` · ${table.currentPlayerName} to act` : ''}
            </Text>
          ) : (
            <Text fontSize="xs" color="gray.500">Waiting</Text>
          )}
        </VStack>

        <VStack spacing={0} align="end" minW="60px">
          <Text fontSize="xs" color="gray.500" lineHeight="1">chips</Text>
          <Text fontSize="sm" color="white" fontWeight="bold" lineHeight="1.2">
            {table.myChips.toLocaleString(undefined, { maximumFractionDigits: 0 })}
          </Text>
        </VStack>

        <Box
          color={turnState === 'turn' ? POKER_COLORS.cyan : 'gray.500'}
          opacity={isCurrent ? 0.4 : 1}
          transition="opacity 0.15s"
          _groupHover={{ opacity: 1, transform: 'translateX(2px)' }}
        >
          <ArrowRight size={16} />
        </Box>
      </HStack>

      {isCurrent && (
        <Text fontSize="9px" color={POKER_COLORS.cyan} mt={1} letterSpacing="wider" fontWeight="bold">
          CURRENTLY VIEWING
        </Text>
      )}
    </Box>
  );
};

export default ActiveSessionsDock;
